import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import {colors} from "nxtconstants";

import {AccordianFaq} from "components";
import {getCalculatorInfoContent} from "./calculatorInfoContent";

const propTypes = {
  slug: PropTypes.string.isRequired
};

const CalculatorInfoSection = ({slug}) => {
  const content = getCalculatorInfoContent(slug);

  if (!content) {
    return null;
  }

  return (
    <Section>
      <Container>
        <MainTitle>{content.title}</MainTitle>

        {content.intro?.map((paragraph, index) => (
          <Paragraph key={`intro-${index}`}>{paragraph}</Paragraph>
        ))}

        {content.sections?.map((section, sectionIndex) => (
          <SectionBlock key={section.heading || sectionIndex}>
            {section.heading ? <SectionTitle>{section.heading}</SectionTitle> : null}

            {section.paragraphs?.map((paragraph, index) => (
              <Paragraph key={`${sectionIndex}-p-${index}`}>{paragraph}</Paragraph>
            ))}

            {section.formula ? <Formula>{section.formula}</Formula> : null}

            {section.paragraphsAfterFormula?.map((paragraph, index) => (
              <Paragraph key={`${sectionIndex}-paf-${index}`}>{paragraph}</Paragraph>
            ))}

            {section.formulaVariables?.length ? (
              <BulletList>
                {section.formulaVariables.map((item, index) => (
                  <li key={`${sectionIndex}-fv-${index}`}>
                    {item.label ? <strong>{item.label}</strong> : null}
                    {item.label ? " " : null}
                    {item.text}
                  </li>
                ))}
              </BulletList>
            ) : null}

            {section.bullets?.length ? (
              <BulletList>
                {section.bullets.map((item, index) => (
                  <li key={`${sectionIndex}-b-${index}`}>
                    {item.label ? <strong>{item.label}</strong> : null}
                    {item.label ? " " : null}
                    {item.text}
                  </li>
                ))}
              </BulletList>
            ) : null}

            {section.table ? (
              <ComparisonTable>
                <thead>
                  <tr>
                    {section.table.headers.map((header, index) => (
                      <th key={`${sectionIndex}-th-${index}`}>{header}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {section.table.rows.map((row, rowIndex) => (
                    <tr key={`${sectionIndex}-tr-${rowIndex}`}>
                      {row.map((cell, cellIndex) => (
                        <td key={`${sectionIndex}-td-${rowIndex}-${cellIndex}`}>{cell}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </ComparisonTable>
            ) : null}

            {section.paragraphsAfterBullets?.map((paragraph, index) => (
              <Paragraph key={`${sectionIndex}-after-${index}`}>{paragraph}</Paragraph>
            ))}

            {section.subsections?.map((subsection, subIndex) => (
              <SubsectionBlock key={`${sectionIndex}-sub-${subIndex}`}>
                {subsection.heading ? (
                  <SubsectionTitle>{subsection.heading}</SubsectionTitle>
                ) : null}

                {subsection.paragraphs?.map((paragraph, index) => (
                  <Paragraph key={`${sectionIndex}-sub-${subIndex}-p-${index}`}>
                    {paragraph}
                  </Paragraph>
                ))}

                {subsection.formula ? <Formula>{subsection.formula}</Formula> : null}

                {subsection.formulaVariables?.length ? (
                  <BulletList>
                    {subsection.formulaVariables.map((item, index) => (
                      <li key={`${sectionIndex}-sub-${subIndex}-fv-${index}`}>
                        {item.label ? <strong>{item.label}</strong> : null}
                        {item.label ? " " : null}
                        {item.text}
                      </li>
                    ))}
                  </BulletList>
                ) : null}

                {subsection.bullets?.length ? (
                  <BulletList>
                    {subsection.bullets.map((item, index) => (
                      <li key={`${sectionIndex}-sub-${subIndex}-b-${index}`}>
                        {item.label ? <strong>{item.label}</strong> : null}
                        {item.label ? " " : null}
                        {item.text}
                      </li>
                    ))}
                  </BulletList>
                ) : null}

                {subsection.paragraphsAfterBullets?.map((paragraph, index) => (
                  <Paragraph key={`${sectionIndex}-sub-${subIndex}-after-${index}`}>
                    {paragraph}
                  </Paragraph>
                ))}
              </SubsectionBlock>
            ))}
          </SectionBlock>
        ))}

        {content.faqs?.items?.length ? (
          <FaqSection>
            {content.faqs.heading ? (
              <FaqSectionTitle>{content.faqs.heading}</FaqSectionTitle>
            ) : null}
            <AccordianFaq
              accordianList={content.faqs.items.map((item) => ({
                title: item.question,
                content: item.answer
              }))}
            />
          </FaqSection>
        ) : null}

        {content.disclaimer ? <Disclaimer>{content.disclaimer}</Disclaimer> : null}
        {content.copyright ? <Copyright>{content.copyright}</Copyright> : null}
      </Container>
    </Section>
  );
};

CalculatorInfoSection.propTypes = propTypes;

const Section = styled.section`
  width: 100%;
  background: ${colors.white};
  padding: 4rem 0 5rem;
  font-family: "Lato";

  @media (max-width: 768px) {
    padding: 3rem 0 4rem;
  }
`;

const Container = styled.div`
  width: 85%;
  max-width: 125rem;
  margin: 0 auto;
  color: ${colors.textBlack};
`;

const MainTitle = styled.h2`
  font-family: "DM Serif Display";
  font-size: 4rem;
  font-weight: 400;
  line-height: 1.25;
  margin: 0 0 2.4rem;

  @media (max-width: 768px) {
    font-size: 2.8rem;
    margin-bottom: 2rem;
  }
`;

const SectionBlock = styled.div`
  &:not(:first-of-type) {
    margin-top: 3.2rem;
  }
`;

const SectionTitle = styled.h3`
  font-family: "Lato";
  font-size: 2.4rem;
  font-weight: 700;
  line-height: 1.35;
  margin: 0 0 1.6rem;
  color: ${colors.textBlack};

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Paragraph = styled.p`
  font-size: 1.6rem;
  line-height: 1.75;
  color: ${colors.textBlack};
  margin: 0 0 1.6rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const FaqSection = styled.div`
  margin-top: 3.2rem;
`;

const FaqSectionTitle = styled.h3`
  font-family: "Lato";
  font-size: 3.2rem;
  font-weight: 700;
  line-height: 1.35;
  margin: 0 0 2rem;
  color: ${colors.textBlack};

  @media (max-width: 768px) {
    font-size: 2.4rem;
    margin-bottom: 1.6rem;
  }
`;

const SubsectionBlock = styled.div`
  margin-top: 2.4rem;
`;

const SubsectionTitle = styled.h4`
  font-family: "Lato";
  font-size: 2rem;
  font-weight: 700;
  line-height: 1.35;
  margin: 0 0 1.2rem;
  color: ${colors.textBlack};

  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

const Formula = styled.p`
  font-size: 1.6rem;
  line-height: 1.75;
  font-weight: 700;
  color: ${colors.textBlack};
  margin: 0 0 1.6rem;
  padding: 1.2rem 1.6rem;
  background: #f8f8f8;
  border-radius: 0.4rem;
`;

const ComparisonTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin: 1.6rem 0 0;
  font-size: 1.5rem;
  line-height: 1.6;

  th,
  td {
    border: 1px solid #e5e5e5;
    padding: 1.2rem 1.4rem;
    text-align: left;
    vertical-align: top;
    color: ${colors.textBlack};
  }

  th {
    font-weight: 700;
    background: #f8f8f8;
  }

  @media (max-width: 768px) {
    display: block;
    overflow-x: auto;
    white-space: nowrap;
  }
`;

const BulletList = styled.ul`
  margin: 0 0 1.6rem;
  padding-left: 2rem;
  list-style: disc;

  li {
    font-size: 1.6rem;
    line-height: 1.75;
    color: ${colors.textBlack};
    margin-bottom: 0.8rem;

    &:last-child {
      margin-bottom: 0;
    }

    strong {
      font-weight: 700;
    }
  }
`;

const Disclaimer = styled.p`
  font-size: 1.4rem;
  line-height: 1.7;
  color: ${colors.textGray};
  margin: 3.2rem 0 1.2rem;
`;

const Copyright = styled.p`
  font-size: 1.4rem;
  line-height: 1.6;
  color: ${colors.textGray};
  margin: 0;
`;

export default CalculatorInfoSection;
